import React, { useEffect, useState } from 'react';
import { fetchHyperDuskProjects, supabase } from '../services/supabase';
import type { HyperDuskProject } from '../types';
import { Loader2, Plus, Clock, CheckCircle2, AlertCircle, ShieldAlert, ChevronLeft, ChevronRight, Scissors, Image as ImageIcon, Film } from 'lucide-react';

type AppView = 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL';

const PAGE_SIZE = 12;
const PROPERTYCUT_APP_URL = 'https://propertycuts.rehub.studio';

/**
 * Open PropertyCuts with a deep-link to import this completed Hyper Dusk
 * project as a fresh draft. Mirrors the same SSO-via-hash pattern used
 * for the cross-app switcher and `Send to Hyper Dusk` flows.
 */
async function openPropertyCutWithProject(projectId: string): Promise<void> {
  const params = new URLSearchParams({
    source: 'hyperdusk',
    projectId,
  });
  let hash = '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      hash =
        `#sso_access_token=${encodeURIComponent(session.access_token)}` +
        `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
    }
  } catch {
    // Fall through; PropertyCuts will prompt for login if SSO can't be forwarded.
  }
  const url = `${PROPERTYCUT_APP_URL}/?${params.toString()}${hash}`;
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win || win.closed) {
    window.location.href = url;
  }
}

interface DashboardProps {
  onNavigate: (view: AppView, projectId?: string) => void;
  currentUserId?: string | null;
}

const statusConfig: Record<
  string,
  { label: string; color: string; icon: typeof Clock }
> = {
  queued: { label: 'Queued', color: 'bg-slate-800 text-slate-300', icon: Clock },
  generating_clips: { label: 'Generating', color: 'bg-amber-900/30 text-amber-400', icon: Loader2 },
  editing: { label: 'Awaiting Approval', color: 'bg-blue-900/30 text-blue-400', icon: Loader2 },
  rendering: { label: 'Rendering', color: 'bg-indigo-900/30 text-indigo-400', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-green-900/30 text-green-400', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-900/30 text-red-400', icon: AlertCircle },
  expired: { label: 'Expired', color: 'bg-slate-800 text-slate-500', icon: Clock },
};

export default function Dashboard({ onNavigate, currentUserId }: DashboardProps) {
  const logoSrc = '/hyperdusk-logo-wordmark-alpha.png';
  const [projects, setProjects] = useState<
    (HyperDuskProject & { image_count: number; thumbnail_url: string | null })[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    async function load() {
      const data = await fetchHyperDuskProjects();
      setProjects(data);
      setLoading(false);
    }
    load();
  }, []);

  const totalPages = Math.max(1, Math.ceil(projects.length / PAGE_SIZE));
  const pagedProjects = projects.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <img src={logoSrc} alt="Hyper Dusk" className="h-8 w-auto mb-2" />
          <h1 className="text-2xl font-bold text-white">Hyper Dusk Projects</h1>
          <p className="text-sm text-slate-400 mt-1">Standalone day-to-night workflow</p>
        </div>
        <button
          onClick={() => onNavigate('NEW_PROJECT')}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-20 animate-fade-in bg-slate-900 border border-slate-800 rounded-xl">
          <img src={logoSrc} alt="Hyper Dusk" className="h-16 w-auto mx-auto mb-4 opacity-90" />
          <h2 className="text-xl font-semibold text-white mb-2">No Hyper Dusk projects yet</h2>
          <p className="text-slate-400 mb-6">Create your first dusk transition now.</p>
          <button
            onClick={() => onNavigate('NEW_PROJECT')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors"
          >
            Start Hyper Dusk
          </button>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pagedProjects.map((project) => {
            const config = statusConfig[project.status] ?? statusConfig.queued;
            const Icon = config.icon;
            const isAnimated = ['generating_clips', 'editing', 'rendering'].includes(project.status);
            const isOtherUser = !!(currentUserId && project.user_id !== currentUserId);

            return (
              <div
                key={project.id}
                className="relative bg-slate-900 rounded-lg border border-slate-800 hover:border-slate-700 transition-all overflow-hidden"
              >
                <button
                  onClick={() => onNavigate('PROJECT_DETAIL', project.id)}
                  className="text-left w-full"
                >
                  <div className="aspect-video bg-slate-800 relative">
                    {project.thumbnail_url ? (
                      <img
                        src={project.thumbnail_url}
                        alt={project.name || 'Project thumbnail'}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <img src={logoSrc} alt="Hyper Dusk" className="h-10 w-auto opacity-70" />
                      </div>
                    )}

                    <span
                      className={`absolute top-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${config.color}`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${isAnimated ? 'animate-spin' : ''}`} />
                      {config.label}
                    </span>

                    {isOtherUser && (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-900/70 text-amber-300 backdrop-blur-sm border border-amber-600/30">
                        <ShieldAlert className="w-3 h-3" />
                        Admin
                      </span>
                    )}
                  </div>

                  <div className="p-4">
                    <h3 className="text-sm font-medium text-white truncate">
                      {project.name || 'Untitled Project'}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-200 bg-indigo-900/40 px-1.5 py-0.5 rounded">
                        {(project.settings?.output_mode ?? 'video') === 'image' ? (
                          <><ImageIcon className="w-3 h-3" /> Image</>
                        ) : (
                          <><Film className="w-3 h-3" /> Video</>
                        )}
                      </span>
                      <p className="text-xs text-slate-500">
                        {new Date(project.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </button>

                {project.status === 'completed' && project.final_video_url && !isOtherUser && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void openPropertyCutWithProject(project.id);
                    }}
                    className="absolute top-2 right-2 p-1.5 text-slate-300 hover:text-teal-300 rounded-md bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
                    title="Send to PropertyCuts"
                  >
                    <Scissors className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </button>
            <span className="text-xs text-slate-400 px-2">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
